import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { AgentThreadRecord } from "@/shared/domain/agentThread";
import { PaginatedResponse } from "@/shared/http";
import type { SearchQueryBody } from "@/types";

export interface UseAdminAgentThreadSearchOptions {
  /**
   * Full POST body for ``v1/admin/threads/search``. Bundles the structured
   * filter ``condition`` (built from the admin filter widget via
   * ``composeFilters``) with ``limit``, ``after``, ``sort_by``, and
   * ``sort_direction``. The hook does no composition of its own — the admin
   * table assembles the whole body and the hook forwards it. The backend
   * enforces field/sort whitelists; an unsupported field returns 400 and
   * surfaces as ``isError``.
   */
  query: SearchQueryBody;
}

/**
 * Cache key for {@link useAdminAgentThreadSearch}. Exported so callers can
 * invalidate, prefetch, or test against the exact key shape the hook uses.
 * Includes ``query`` by value, so any change to filter/sort/pagination yields
 * a distinct cache entry.
 */
export function getQueryKey(query: SearchQueryBody) {
  return ["adminAgentThreadSearch", query] as const;
}

/**
 * Paginated structured search over every agent thread, for the admin thread
 * explorer. The admin sibling of {@link useAgentThreadSearch}: it hits the
 * admin-only ``v1/admin/threads/search`` endpoint (which spans all orgs and is
 * gated purely on admin identity), so there is no ``orgId`` and the query
 * never disables itself on missing org context.
 *
 * The query key includes ``query`` by value (see {@link getQueryKey}), so any
 * change to filter/sort/pagination yields a distinct cache entry and refetches
 * on the next render. Results are considered fresh for 30 seconds.
 */
export function useAdminAgentThreadSearch(
  options: UseAdminAgentThreadSearchOptions,
): UseQueryResult<PaginatedResponse<AgentThreadRecord>, Error> {
  const { adminService } = useDomainServices();

  return useQuery<PaginatedResponse<AgentThreadRecord>, Error>({
    queryKey: getQueryKey(options.query),
    queryFn: ({ signal }) =>
      adminService.searchAdminAgentThreads(options.query, {
        abortSignal: signal,
      }),
    staleTime: 30_000,
  });
}
